export interface Milestone {
  title: string;
  description: string;
  logicResults: string;
  tech?: string[];
}

export interface Project {
  name: string;
  role: string;
  description: string;
  url?: string;
  achievements: string[];
}

export interface SkillGroup {
  category: string;
  skills: string[];
}

export interface ManifestoPoint {
  principle: string;
  statement: string;
}

// Nueva interfaz para plasmar tu trayectoria real
export interface EvolutionStep {
  period: string;
  stage: string;
  context: string;
}

export interface ProfileData {
  hero: {
    name: string;
    role: string;
    tagline: string;
    about: string;
  };
  evolution: EvolutionStep[];
  milestones: Milestone[];
  projects: Project[];
  infrastructureStack: SkillGroup[];
  intelligenceStack: SkillGroup[];
  legacyStack: SkillGroup[];
  hardware: {
    title: string;
    blurb: string;
    items: { name: string; desc: string }[];
  };
  manifesto: ManifestoPoint[];
  ui: {
    logicResults: string;
    systemMilestones: string;
    manifestoTitle: string;
    evolutionTitle: string;
    stackMetal: string;
    stackIntelligence: string;
    stackLegacy: string;
    analysisComplete: string;
    helpTip: string;
    aboutTitle: string;
  };
}

export const profileData: Record<string, ProfileData> = {
  en: {
    hero: {
      name: "Jacob G.A",
      role: "Systems Architect | AI Engineer | Hacker by Design",
      tagline: "Resilient Infrastructure. Strategic Execution. Sovereignty through Metal.",
      about: "19 years of stripping down complexity. Forged in the high-pressure environments of global banking and evolved through the necessity of a builder's grind. I don't just consume frameworks; I dissect them to reclaim control. My career is a continuous feedback loop between high-stakes software architecture, hardware electronics (IoT), and offensive security research. I build systems that scale because I've spent nearly two decades seeing exactly how, why, and where they break. I have mentored engineers and led projects to production. My base is Java/Kotlin (up to Java 23, creator of the open-source Koupper), I work with Python and shell scripting daily, I am currently learning Rust, I design architectures on AWS and experiment with local AI (Hugging Face). I have shipped Quizztea, Ztreamers and The Developer Ninja to production. I founded my own company and run my own platforms. I teach courses and run a community to teach programming. Geek at heart: videogames, 3D rendering, electronics, robotics and 3D printing. I enjoy sharing what I know and learning from others."
    },
    evolution: [
      {
        period: "The Foundation",
        stage: "Scholastic Grind & Social Hacking",
        context: "Balanced manual labor with technical studies. Learned that social engineering and resilience are as vital as syntax."
      },
      {
        period: "Corporate Forge",
        stage: "Global Banking & Fintech (Citi, BBVA, Scotiabank, Wipro, GFT)",
        context: "Hardened under strict regulations and high-pressure legacy/modern hybrids. Managed massive data volumes and mission-critical systems."
      },
      {
        period: "Innovation Hub",
        stage: "Complex Integrations (Travelport, Igly)",
        context: "Solving architectural puzzles in logistics and product development. Leveraged deep tech to build new platforms from the ground up."
      },
      {
        period: "The Sovereign Era",
        stage: "Edge AI & Autonomous Systems",
        context: "Killing cloud dependencies. Integrating LLMs (Python/Kotlin) with local metal and physical hardware for total data autonomy."
      }
    ],
    milestones: [
      {
        title: "Koupper & Octopus (Sovereign Orchestration)",
        description: "Designed and implemented a Distributed Scripting Framework and an Orchestration Engine to bypass commercial middleware.",
        logicResults: "Eliminated third-party dependencies. Reduced orchestration overhead by 40%. Full control of the execution lifecycle."
      },
      {
        title: "High-Concurrency SaaS Scaling",
        description: "Architected 3 global platforms (The Developer Ninja, Quizztea, Ztreamers) using serverless and mixed architectures.",
        logicResults: "Sub-second global latency. Infrastructure cost-mapped directly to usage. Zero-downtime deployment pipelines."
      },
      {
        title: "Edge AI & Hardware Optimization",
        description: "Moving intelligence to the edge to kill latency and reclaim data control. Optimized models for NVIDIA Jetson and local hardware.",
        logicResults: "70% reduction in inference costs. Privacy-first architecture. Real-time industrial computer vision without cloud Round-Trip Time (RTT)."
      },
      {
        title: "Electronics & IoT Prototyping",
        description: "Hardware prototypes with ESP32/Arduino and NVIDIA Jetson: sensors, MQTT and 3D-printed enclosures for real-world devices.",
        logicResults: "Working Edge AI demos on real metal. Privacy-first data paths. Prototype-to-product speed."
      }
    ],
    projects: [
      {
        name: "The Developer Ninja",
        role: "Founder & Lead Architect",
        description: "AI-powered educational platform.",
        achievements: [
          "End-to-end system design using Node.js and AWS Serverless.",
          "Implemented advanced RAG pipelines for personalized local intelligence.",
          "Courses and a dev community to teach programming."
        ],
        url: "https://thedeveloper.ninja"
      },
      {
        name: "Quizztea",
        role: "Platform Architect",
        description: "Enterprise-grade interactive analytics.",
        achievements: [
          "Mixed architecture (Monolith + Microservices) using Kotlin/Grizzly.",
          "Hardened OAuth 2.0 + JWT authentication ecosystems."
        ],
        url: "https://quizztea.com"
      },
      {
        name: "Ztreamers",
        role: "Founder & Architect",
        description: "Streaming platform in production.",
        achievements: [
          "End-to-end architecture and production release.",
          "Frontend with Vue 2 and backend services."
        ]
      }
    ],
    infrastructureStack: [
      {
        category: "Core High-Performance",
        skills: ["Java 8 → 23 (JDK/JRE tuning)", "Kotlin (JVM) — Koupper framework", "Python (AI/Backend)", "Shell Scripting (Bash/Zsh)", "Rust (currently learning)", "JavaScript/TypeScript"]
      },
      {
        category: "Frontend",
        skills: ["React", "Vue", "HTML/CSS"]
      },
      {
        category: "Edge AI & Hardware",
        skills: ["NVIDIA Jetson", "TensorRT", "CUDA", "Hugging Face", "Ollama / vLLM", "Model Quantization", "Local LLMs", "Gemini", "Claude", "Cursor", "OpenCode", "Experimental Go"]
      },
      {
        category: "Infrastructure & Sovereignty",
        skills: ["AWS (Serverless/EC2 — Architecture Design)", "Koupper (Open source)", "Octopus (Internal)", "Docker", "Kubernetes", "Linux Kernel Tuning", "SQL Systems"]
      }
    ],
    intelligenceStack: [
      {
        category: "Security & Research",
        skills: ["Protocol Analysis", "Network Forensics", "Offensive Security Mindset", "API Hardening", "OAuth 2.0/OIDC"]
      },
      {
        category: "Electronics & IoT",
        skills: ["Hardware Prototyping", "Embedded Systems", "MQTT", "ESP32/Arduino", "PCB Awareness", "3D Printing", "Robotics", "Open Source (Koupper)"]
      }
    ],
    legacyStack: [
      {
        category: "Web & Enterprise Ecosystems",
        skills: ["Vue 2 (Ztreamers)", "PHP (Laravel)", "JBoss", "Websphere", "Oracle/DB2"]
      }
    ],
    hardware: {
      title: "Hardware Lab",
      blurb: "This is where software touches the physical world: I design, print and deploy intelligence on real devices.",
      items: [
        { name: "NVIDIA Jetson", desc: "I run local LLMs (Ollama / vLLM) and computer vision at the edge. TensorRT, CUDA and quantized models: zero cloud, zero latency." },
        { name: "ESP32 / Arduino", desc: "Custom firmware, sensors and MQTT for IoT and home automation prototypes." },
        { name: "3D Printing", desc: "I design and print enclosures and custom parts for my devices: from CAD to hand." },
        { name: "Robotics", desc: "Experiments with motors, actuators and autonomous systems driven by local AI." }
      ]
    },
    manifesto: [
      {
        principle: "Technological Sovereignty",
        statement: "Own the stack or the stack owns you. Dependencies are liabilities."
      },
      {
        principle: "Efficiency over Abstraction",
        statement: "Optimization starts at the metal. If it's slow, your abstractions are leaking."
      },
      {
        principle: "Security as an Instinct",
        statement: "A system isn't finished until it's hardened. Build as if you were the one trying to breach it."
      },
      {
        principle: "The Hacker Ethos",
        statement: "Never stop at 'it works'. Find out WHY it works, and how to make it work better."
      }
    ],
    ui: {
      logicResults: "Logic Results",
      systemMilestones: "Things I have built",
      manifestoTitle: "The Jacob G.A Manifesto",
      evolutionTitle: "My journey",
      stackMetal: "What I build with",
      stackIntelligence: "Security and connected devices",
      stackLegacy: "Also in my toolbox",
      analysisComplete: "Done — that is how I work.",
      helpTip: "Tap Help or type help to see more.",
      aboutTitle: "About me"
    }
  },
  es: {
    hero: {
      name: "Jacob G.A",
      role: "Arquitecto de Sistemas | Ingeniero de IA | Hacker by Design",
      tagline: "Infraestructura Resiliente. Ejecución Estratégica. Soberanía vía Metal.",
      about: "19 años desmantelando complejidad. Forjado en los entornos de alta presión de la banca global y evolucionado por la necesidad de construir desde cero. No consumo frameworks; los disecciono para recuperar el control. Mi carrera es un bucle constante entre arquitectura de software de alto riesgo, electrónica de hardware (IoT) e investigación de seguridad ofensiva. Construyo sistemas que escalan porque he pasado casi dos décadas viendo exactamente cómo, por qué y dónde se rompen. He mentoreado a ingenieros y liderado proyectos hasta producción. Mi base es Java/Kotlin (hasta Java 23, creador del open-source Koupper), trabajo con Python y shell scripting a diario, actualmente aprendo Rust, diseño arquitecturas en AWS y experimento con IA local (Hugging Face). He llevado a producción Quizztea, Ztreamers y The Developer Ninja. Fundé mi propia empresa y mis plataformas. Doy cursos y tengo mi comunidad para enseñar a programar. Geek de corazón: videojuegos, render 3D, electrónica, robótica e impresión 3D. Me gusta compartir lo que sé y aprender de los demás."
    },
    evolution: [
      {
        period: "La Fundación",
        stage: "Grind Escolar y Hacking Social",
        context: "Equilibré el trabajo manual con estudios técnicos. Aprendí que la ingeniería social y la resiliencia son tan vitales como la sintaxis."
      },
      {
        period: "Forja Corporativa",
        stage: "Banca Global y Fintech (Citi, BBVA, Scotiabank, Wipro, GFT)",
        context: "Endurecido bajo regulaciones estrictas y sistemas híbridos legacy/modernos. Gestioné volúmenes masivos de datos y sistemas de misión crítica."
      },
      {
        period: "Hub de Innovación",
        stage: "Integraciones Complejas (Travelport, Igly)",
        context: "Resolviendo acertijos arquitectónicos en logística y desarrollo de productos. Aproveché el deep tech para construir nuevas plataformas desde cero."
      },
      {
        period: "La Era Soberana",
        stage: "Edge AI y Sistemas Autónomos",
        context: "Eliminando dependencias de la nube. Integrando LLMs (Python/Kotlin) con metal local y hardware físico para una autonomía total de datos."
      }
    ],
    milestones: [
      {
        title: "Koupper & Octopus (Orquestación Soberana)",
        description: "Diseñé e implementé un Framework de Scripting Distribuido y un Motor de Orquestación para bypass de middleware comercial.",
        logicResults: "Eliminé dependencias de terceros. Reduje el overhead de orquestación en un 40%. Control total del ciclo de vida de ejecución."
      },
      {
        title: "Escalado de SaaS de Alta Concurrencia",
        description: "Arquitecté 3 plataformas globales (The Developer Ninja, Quizztea, Ztreamers) usando serverless y arquitecturas mixtas.",
        logicResults: "Latencia global sub-segundo. Costo de infraestructura mapeado directamente al uso. Pipelines de despliegue sin tiempo de inactividad."
      },
      {
        title: "Edge AI y Optimización de Hardware",
        description: "Moviendo la inteligencia al borde para matar la latencia y recuperar el control del dato. Modelos optimizados para NVIDIA Jetson y hardware local.",
        logicResults: "Reducción del 70% en costos de inferencia. Arquitectura privacy-first. Visión computacional industrial en tiempo real sin RTT de nube."
      },
      {
        title: "Electrónica e IoT en Metal",
        description: "Prototipos de hardware con ESP32/Arduino y NVIDIA Jetson: sensores, MQTT y carcasas impresas en 3D para dispositivos reales.",
        logicResults: "Demos de Edge AI funcionando en metal real. Rutas de datos privacy-first. Velocidad de prototipo a producto."
      }
    ],
    projects: [
      {
        name: "The Developer Ninja",
        role: "Fundador y Arquitecto Líder",
        description: "Plataforma educativa impulsada por IA.",
        achievements: [
          "Diseño de sistema end-to-end usando Node.js y AWS Serverless.",
          "Implementación de pipelines RAG avanzados para inteligencia local personalizada.",
          "Cursos y comunidad para enseñar a programar."
        ],
        url: "https://thedeveloper.ninja"
      },
      {
        name: "Quizztea",
        role: "Arquitecto de Plataforma",
        description: "Analítica interactiva de grado empresarial.",
        achievements: [
          "Arquitectura mixta (Monolito + Microservicios) usando Kotlin/Grizzly.",
          "Ecosistemas de autenticación OAuth 2.0 + JWT endurecidos."
        ],
        url: "https://quizztea.com"
      },
      {
        name: "Ztreamers",
        role: "Fundador y Arquitecto",
        description: "Plataforma de streaming en producción.",
        achievements: [
          "Arquitectura end-to-end y salida a producción.",
          "Frontend con Vue 2 y servicios backend."
        ]
      }
    ],
    infrastructureStack: [
      {
        category: "Core de Alto Rendimiento",
        skills: ["Java 8 → 23 (JDK/JRE)", "Kotlin (JVM) — framework Koupper", "Python (AI/Backend)", "Shell Scripting (Bash/Zsh)", "Rust (aprendiendo actualmente)", "JavaScript/TypeScript"]
      },
      {
        category: "Frontend",
        skills: ["React", "Vue", "HTML/CSS"]
      },
      {
        category: "Edge AI y Hardware",
        skills: ["NVIDIA Jetson", "TensorRT", "CUDA", "Hugging Face", "Ollama / vLLM", "Cuantización de Modelos", "LLMs Locales", "Gemini", "Claude", "Cursor", "OpenCode", "Go Experimental"]
      },
      {
        category: "Infraestructura y Soberanía",
        skills: ["AWS (Serverless/EC2 — Diseño de arquitecturas)", "Koupper (Open source)", "Octopus (Interno)", "Docker", "Kubernetes", "Tuning de Kernel Linux", "Sistemas SQL"]
      }
    ],
    intelligenceStack: [
      {
        category: "Seguridad e Investigación",
        skills: ["Análisis de Protocolos", "Forense de Red", "Mentalidad de Seguridad Ofensiva", "Hardening de APIs", "OAuth 2.0/OIDC"]
      },
      {
        category: "Electrónica e IoT",
        skills: ["Hardware Prototyping", "Embedded Systems", "MQTT", "ESP32/Arduino", "Conciencia de PCB", "Impresión 3D", "Robótica", "Open Source (Koupper)"]
      }
    ],
    legacyStack: [
      {
        category: "Ecosistemas Web y Empresariales",
        skills: ["Vue 2 (Ztreamers)", "PHP (Laravel)", "JBoss", "Websphere", "Oracle/DB2"]
      }
    ],
    hardware: {
      title: "Laboratorio de Hardware",
      blurb: "Aquí el software toca el mundo físico: diseño, imprimo y despliego inteligencia en dispositivos reales.",
      items: [
        { name: "NVIDIA Jetson", desc: "Corro LLMs locales (Ollama / vLLM) y visión por computadora en el borde. TensorRT, CUDA y modelos cuantizados: cero nube, cero latencia." },
        { name: "ESP32 / Arduino", desc: "Firmware propio, sensores y MQTT para prototipos de IoT y domótica." },
        { name: "Impresión 3D", desc: "Diseño e imprimo carcasas y piezas a medida para mis dispositivos: del CAD a la mano." },
        { name: "Robótica", desc: "Experimentos con motores, actuadores y sistemas autónomos guiados por IA local." }
      ]
    },
    manifesto: [
      {
        principle: "Soberanía Tecnológica",
        statement: "Eres dueño del stack o el stack es dueño de ti. Las dependencias son pasivos."
      },
      {
        principle: "Eficiencia sobre Abstracción",
        statement: "La optimización comienza en el metal. Si es lento, tus abstracciones tienen fugas."
      },
      {
        principle: "Seguridad como Instinto",
        statement: "Un sistema no está terminado hasta que está endurecido. Construye como si fueras tú quien intenta vulnerarlo."
      },
      {
        principle: "El Ethos Hacker",
        statement: "Nunca te detengas en 'funciona'. Descubre POR QUÉ funciona, y cómo hacerlo funcionar mejor."
      }
    ],
    ui: {
      logicResults: "Por qué importa",
      systemMilestones: "Lo que he construido",
      manifestoTitle: "El Manifiesto de Jacob G.A",
      evolutionTitle: "Mi camino",
      stackMetal: "Con qué construyo",
      stackIntelligence: "Seguridad y aparatos conectados",
      stackLegacy: "También manejo",
      analysisComplete: "Listo — así trabajo.",
      helpTip: "Toca Ayuda o escribe help para ver más.",
      aboutTitle: "Sobre mí"
    }
  }
};
